import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Box, Card, CardContent, Chip, Skeleton, Stack, Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ResponsiveLine } from '@nivo/line';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import config from '../../config';
import { getLocalUser } from '../../utils/localUser';
import { useDisplayNames } from '../../utils/displayNames';

/*
 * The driver reveal: the goal's strongest validated driver drawn ON TOP
 * of the target it predicts, with the driver shifted forward by its
 * discovered lead time. When the peaks line up, the user is looking at
 * the reason driver discovery exists — "this signal moves first, your
 * metric follows N days later" — as a picture instead of a claim.
 *
 * Reads the same /goals/:id/graph payload the relationship map uses
 * (via its redux slice, no extra fetch) to pick the top validated
 * signal edge, then pulls both series from /signal/overview's pins.
 * Renders nothing when there is no validated signal driver — the graph
 * and influence panels below still tell the story.
 */

// Proven, healthy relationships only — this card makes a visual claim.
const CLEARED = new Set(['validated', 'revalidated']);

const humanSpan = (seconds) => {
  if (!seconds) return null;
  if (seconds >= 86400) {
    const d = Math.round(seconds / 86400);
    return `${d} day${d === 1 ? '' : 's'}`;
  }
  if (seconds >= 3600) {
    const h = Math.round(seconds / 3600);
    return `${h} hour${h === 1 ? '' : 's'}`;
  }
  const m = Math.max(1, Math.round(seconds / 60));
  return `${m} min`;
};

const pickTopDriverEdge = (graph) => {
  const edges = (graph?.edges || []).filter((e) => (
    CLEARED.has(e.lifecycle)
    && e?.source?.key
    && e.source.kind !== 'event'      // events have no continuous series
    && !e.mediator_key                // direct pull on the target
    && e.source.key !== graph.target_key
  ));
  if (!edges.length) return null;
  return [...edges].sort((a, b) => (
    ((b.impact ?? 0) - (a.impact ?? 0)) || ((b.mu ?? 0) - (a.mu ?? 0))
  ))[0];
};

const toPoints = (bins) => (bins || [])
  .map((b) => ({ t: Date.parse(b.from), v: Number(b.avgValue) }))
  .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v))
  .sort((a, b) => a.t - b.t);

// Min-max normalize onto 0..1 so wildly different scales compare as
// SHAPES (raw values stay in the tooltip).
const normalize = (pts) => {
  let lo = Infinity;
  let hi = -Infinity;
  pts.forEach((p) => { lo = Math.min(lo, p.v); hi = Math.max(hi, p.v); });
  const span = hi - lo || 1;
  return pts.map((p) => ({ x: new Date(p.t), y: (p.v - lo) / span, raw: p.v }));
};

const DriverRevealChart = ({ goalId, targetKey }) => {
  const theme = useTheme();
  const nameOf = useDisplayNames();

  // Same staleness rule as the relationship map: only trust the graph
  // once its goal_id matches the goal on screen.
  const rawGraph = useSelector((s) => s.discovery_graph?.response);
  const graph = rawGraph && rawGraph.goal_id === goalId ? rawGraph : null;

  const edge = useMemo(() => (graph ? pickTopDriverEdge(graph) : null), [graph]);
  const driverKey = edge?.source?.key || null;
  const interval = graph?.interval_seconds || 0;
  const lagBins = edge?.lag_bins || 0;
  const lagSeconds = lagBins * interval;

  const [fetch, setFetch] = useState({ loading: false, pinned: null });
  useEffect(() => {
    if (!driverKey || !targetKey) return undefined;
    let cancelled = false;
    setFetch({ loading: true, pinned: null });
    axios.get(
      `${config.API_URL}/signal/overview?number_of_bins=200`
      + `&pins=${encodeURIComponent(`${targetKey},${driverKey}`)}`,
      { headers: getLocalUser() },
    ).then((res) => {
      if (cancelled) return;
      setFetch({ loading: false, pinned: res?.data?.data?.data?.pinned || [] });
    }).catch(() => {
      if (cancelled) return;
      setFetch({ loading: false, pinned: null });
    });
    return () => { cancelled = true; };
  }, [driverKey, targetKey]);

  const chart = useMemo(() => {
    if (!Array.isArray(fetch.pinned) || !edge) return null;
    const binsOf = (key) => fetch.pinned.find((r) => r.key === key)?.bins;
    const targetPts = toPoints(binsOf(targetKey));
    // The driver is drawn where its influence LANDS: value at t plotted
    // at t + lag. Aligned peaks = real predictive pull, visibly.
    const lagMs = lagSeconds * 1000;
    const driverPts = toPoints(binsOf(driverKey))
      .map((p) => ({ ...p, t: p.t + lagMs }));
    if (targetPts.length < 8 || driverPts.length < 8) return null;
    // Clip both to the overlapping window so the lines cover the same span.
    const lo = Math.max(targetPts[0].t, driverPts[0].t);
    const hi = Math.min(
      targetPts[targetPts.length - 1].t,
      driverPts[driverPts.length - 1].t,
    );
    if (hi <= lo) return null;
    const clip = (pts) => pts.filter((p) => p.t >= lo && p.t <= hi);
    const target = normalize(clip(targetPts));
    const driver = normalize(clip(driverPts));
    if (target.length < 8 || driver.length < 8) return null;
    return { target, driver };
  }, [fetch.pinned, edge, targetKey, driverKey, lagSeconds]);

  if (!edge || !targetKey) return null;

  const targetName = nameOf(targetKey);
  const driverName = nameOf(driverKey);
  const lagLabel = lagSeconds > 0 ? humanSpan(lagSeconds) : null;
  const negative = edge.direction === 'negative';
  const confidencePct = edge.mu != null ? Math.round(edge.mu * 100) : null;

  const targetColor = theme.palette.primary.main;
  const driverColor = theme.palette.warning.main;

  const series = chart ? [
    { id: targetName, data: chart.target },
    {
      id: lagLabel ? `${driverName} (shifted +${lagLabel})` : driverName,
      data: chart.driver,
    },
  ] : [];

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
          <InsightsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: '0 1 auto' }}>
            Your top driver, on your target
          </Typography>
          <Tooltip
            title={negative
              ? 'Inverse relationship: when the driver rises, the target tends to fall.'
              : 'When the driver moves, the target tends to follow in the same direction.'}
            arrow
          >
            <Chip
              size="small"
              variant="outlined"
              color={negative ? 'error' : 'success'}
              icon={negative ? <TrendingDownIcon /> : <TrendingUpIcon />}
              label={negative ? 'moves against' : 'moves with'}
              sx={{ height: 20 }}
            />
          </Tooltip>
          {lagLabel && (
            <Tooltip title="How far ahead the driver moves before the target reacts — its lead time." arrow>
              <Chip size="small" variant="outlined" label={`leads by ${lagLabel}`} sx={{ height: 20 }} />
            </Tooltip>
          )}
          {confidencePct != null && (
            <Tooltip title="Discovery's confidence that this relationship is real (validated out-of-sample, not just correlated)." arrow>
              <Chip size="small" variant="outlined" label={`${confidencePct}% confidence`} sx={{ height: 20 }} />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {driverName} overlaid on {targetName}
          {lagLabel ? `, shifted forward by its ${lagLabel} lead time` : ''} —
          both normalized to compare shape. Where the peaks line up is the
          predictive pull your models feed on.
        </Typography>

        {fetch.loading && <Skeleton variant="rounded" height={220} />}

        {!fetch.loading && chart && (
          <Box sx={{ height: 220 }}>
            <ResponsiveLine
              data={series}
              colors={[targetColor, driverColor]}
              margin={{ top: 8, right: 12, bottom: 44, left: 12 }}
              xScale={{ type: 'time', useUTC: false }}
              yScale={{ type: 'linear', min: -0.02, max: 1.02 }}
              axisLeft={null}
              axisBottom={{ format: '%b %d', tickValues: 6, tickSize: 0, tickPadding: 10 }}
              enableGridX={false}
              enableGridY={false}
              curve="monotoneX"
              lineWidth={2}
              enablePoints={false}
              enableArea={false}
              useMesh
              enableSlices="x"
              theme={{
                background: 'transparent',
                text: {
                  fill: theme.palette.text.secondary,
                  fontSize: 10,
                  fontFamily: theme.typography.fontFamily,
                },
                axis: {
                  ticks: {
                    line: { stroke: 'transparent' },
                    text: { fill: theme.palette.text.secondary, fontSize: 10 },
                  },
                  domain: { line: { stroke: 'transparent' } },
                },
                crosshair: {
                  line: {
                    stroke: theme.palette.text.secondary,
                    strokeWidth: 1,
                    strokeOpacity: 0.45,
                    strokeDasharray: '3 4',
                  },
                },
              }}
              sliceTooltip={({ slice }) => (
                <Box sx={{
                  px: 1.25, py: 0.75, borderRadius: '8px',
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: 4, fontSize: 11,
                }}>
                  {slice.points.map((p) => (
                    <Stack key={p.id} direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: p.serieColor }} />
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {p.serieId}:
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                        {Number(p.data.raw ?? p.data.y).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              )}
            />
          </Box>
        )}

        {!fetch.loading && !chart && (
          <Typography variant="caption" color="text.disabled">
            Series preview unavailable for this driver.
          </Typography>
        )}

        {chart && (
          <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
            {series.map((sr, i) => (
              <Stack key={sr.id} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{
                  width: 14, height: 3, borderRadius: 2,
                  bgcolor: i === 0 ? targetColor : driverColor,
                }} />
                <Typography variant="caption" sx={{ color: alpha(theme.palette.text.primary, 0.7) }}>
                  {sr.id}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default DriverRevealChart;
